import { A } from '@ember/array';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { Ability } from 'ember-can';
import type AuthzService from 'garaje/services/authz';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';
import { GLOBAL_ADMIN, LOCATION_ADMIN } from 'garaje/utils/roles';
import { Permission } from 'garaje/utils/ui-permissions';
import _intersection from 'lodash/intersection';

const CAN_CREATE_ROLES = [GLOBAL_ADMIN];
const CAN_EDIT_LOCATION_SETTINGS = [GLOBAL_ADMIN, LOCATION_ADMIN];

export default class LocationAbility extends Ability {
  @service declare currentAdmin: CurrentAdminService;
  @service declare state: StateService;
  @service declare authz: AuthzService;
  @service declare featureFlags: FeatureFlagsService;

  get canCreate(): boolean {
    if (!this.state.hasAnyFeatures) {
      return false;
    }

    if (this.featureFlags.isEnabled('location-authz-permissions')) {
      return this.authz.hasPermissionAtCurrentCompany(Permission.ACCOUNT_MANAGEMENT_LOCATION_CREATE);
    } else {
      const roleNames = this.currentAdmin.roleNames;
      if (isPresent(_intersection(CAN_CREATE_ROLES, roleNames))) {
        return true;
      }

      const companyRoles = this.currentAdmin.companyRoles || [];

      return A(companyRoles).filterBy('roleName', GLOBAL_ADMIN).length > 0;
    }
  }

  get canUpdate(): boolean {
    if (!this.featureFlags.isEnabled('location-authz-permissions')) return true;

    return this.authz.hasPermissionAtCurrentLocation(Permission.ACCOUNT_MANAGEMENT_LOCATION_UPDATE);
  }

  get canUpdateAddress(): boolean {
    // If feature flag is disabled, fallback to existing behavior
    if (!this.featureFlags.isEnabled('visitors-permission-manage-location-settings')) return this.canUpdate;

    const permissions = [
      Permission.VISITORS_LOCATION_CONFIGURATION_UPDATE_ADDRESS,
      Permission.ACCOUNT_MANAGEMENT_LOCATION_UPDATE,
    ];

    return this.authz.hasAnyPermissionAtCurrentLocation(permissions);
  }

  get canUpdateBranding(): boolean {
    if (!this.featureFlags.isEnabled('visitors-permission-manage-location-settings'))
      return this._hasPermissionToUpdateWithLegacyRole;

    return this.authz.hasPermissionAtCurrentLocation(Permission.VISITORS_LOCATION_CONFIGURATION_UPDATE_BRANDING);
  }

  get canGenerateApiKey(): boolean {
    if (!this.featureFlags.isEnabled('visitors-permission-manage-location-settings'))
      return this._hasPermissionToUpdateWithLegacyRole;

    return this.authz.hasPermissionAtCurrentLocation(Permission.VISITORS_LOCATION_CONFIGURATION_GENERATE_KEY);
  }

  get canUpdateLanguage(): boolean {
    if (!this.featureFlags.isEnabled('visitors-permission-manage-location-settings'))
      return this._hasPermissionToUpdateWithLegacyRole;

    return this.authz.hasPermissionAtCurrentLocation(Permission.VISITORS_LOCATION_CONFIGURATION_UPDATE_LANGUAGE);
  }

  get canUpdateCapacity(): boolean {
    if (!this.state.features?.canAccessCapacityLimits) return false;
    if (!this.featureFlags.isEnabled('visitors-permission-manage-location-settings'))
      return this._hasPermissionToUpdateWithLegacyRole;

    return this.authz.hasPermissionAtCurrentLocation(Permission.VISITORS_LOCATION_CONFIGURATION_UPDATE_CAPACITY);
  }

  get canUpdateWorkplaceUtilization(): boolean {
    if (!this.featureFlags.isEnabled('visitors-workplace-details')) return false;
    if (!this.featureFlags.isEnabled('visitors-permission-manage-location-settings'))
      return this._hasPermissionToUpdateWithLegacyRole;

    return this.authz.hasPermissionAtCurrentLocation(
      Permission.VISITORS_LOCATION_CONFIGURATION_UPDATE_WORKPLACE_UTILIZATION,
    );
  }

  get canDeactivate(): boolean {
    if (!this.featureFlags.isEnabled('visitors-permission-manage-location-settings'))
      return this._hasPermissionToUpdateWithLegacyRole;

    return this.authz.hasPermissionAtCurrentLocation(Permission.VISITORS_LOCATION_CONFIGURATION_UPDATE_DISABLED);
  }

  get canReadDisabledLocation(): boolean {
    return this.authz.hasPermissionAtCurrentLocation(Permission.ACCOUNT_MANAGEMENT_DISABLED_LOCATION_READ);
  }

  get _hasPermissionToUpdateWithLegacyRole(): boolean {
    const { roleNames } = this.currentAdmin;
    return isPresent(_intersection(CAN_EDIT_LOCATION_SETTINGS, roleNames));
  }
}
